import { Badge, BadgeText } from "@repo/components/src/ui/badge";
import { Box } from "@repo/components/src/ui/box";
import { Button, ButtonIcon, ButtonText } from "@repo/components/src/ui/button";
import { Card } from "@repo/components/src/ui/card";
import { Heading } from "@repo/components/src/ui/heading";
import { HStack } from "@repo/components/src/ui/hstack";
import { Icon } from "@repo/components/src/ui/icon";
import { Text } from "@repo/components/src/ui/text";
import { VStack } from "@repo/components/src/ui/vstack";
import {
  formatMoneyFromFragment,
  formatSimpleMoney,
} from "@repo/components/src/utils/money";
import { formatPercentage } from "@repo/components/src/utils/numbers";
import { SubscriptionsPricingNameChoices } from "@repo/graphql/gql/graphql";
import { Check, MessageCircle } from "lucide-react-native";
import React, { useMemo } from "react";
import { Linking } from "react-native";
import { PriceablePlan, priceFor } from "@/utils/planPrices";
import { ENTERPRISE_PLAN_CODE } from "./planConstants";

type Plan = PriceablePlan;

type Props = {
  plan: Plan;
  interval: SubscriptionsPricingNameChoices;
  countryId: string | null;
  /** Country's VAT/tax label (e.g. "IVA", "IGV") — replaces the hardcoded "IVA" copy. */
  vatLabel: string;
  /** External sales contact link for the country. Empty string when the country has none. */
  salesContactUrl: string;
  recommended: boolean;
  loading: boolean;
  onHire: (
    priceId: string,
    planName: string,
    paymentProviderCode: string | null,
  ) => void;
  /** When set, shows a "Pagando {amount} hoy" line under the description. */
  todayAmount?: string;
};

const PlanTierCard = React.memo((props: Props) => {
  const {
    plan,
    interval,
    countryId,
    vatLabel,
    salesContactUrl,
    recommended,
    loading,
    onHire,
    todayAmount,
  } = props;

  const isEnterprise = plan.code?.enum === ENTERPRISE_PLAN_CODE;
  const isYearly = interval === SubscriptionsPricingNameChoices.Yearly;

  const monthlyPrice = useMemo(
    () => priceFor(plan, SubscriptionsPricingNameChoices.Monthly, countryId),
    [plan, countryId],
  );
  const yearlyPrice = useMemo(
    () => priceFor(plan, SubscriptionsPricingNameChoices.Yearly, countryId),
    [plan, countryId],
  );
  const price = isYearly ? (yearlyPrice ?? monthlyPrice) : monthlyPrice;

  // No price listed for this country in either interval — the plan can only
  // be sold through a direct sales conversation, not the self-serve CTA.
  const unpriced = !monthlyPrice && !yearlyPrice;
  const needsContact = isEnterprise || unpriced;

  const savingsRatio = useMemo(() => {
    const m = monthlyPrice?.totalValue?.amount;
    const y = yearlyPrice?.totalValue?.amount;
    if (!m || !y) return null;
    const annualized = m * 12;
    if (y >= annualized) return null;
    return (annualized - y) / annualized;
  }, [monthlyPrice, yearlyPrice]);

  // Show the per-month equivalent as the headline price for annual plans
  // ("US$16/mo · Billed annually, or US$19 monthly" pattern).
  const yearlyPerMonth = useMemo(() => {
    const y = yearlyPrice?.totalValue?.amount;
    const currency = yearlyPrice?.totalValue?.currency?.code;
    if (!y || !currency) return null;
    return formatSimpleMoney({ amount: String(y / 12), currency });
  }, [yearlyPrice]);

  const renderPrice = () => {
    if (unpriced) {
      return (
        <VStack>
          <Heading size="2xl" className="tracking-tight text-typography-900">
            Precio a medida
          </Heading>
        </VStack>
      );
    }

    if (isEnterprise) {
      return (
        <VStack>
          <HStack space="xs" className="items-baseline">
            <Text size="sm" className="text-typography-500">
              Desde
            </Text>
            <Heading size="2xl" className="tracking-tight text-typography-900">
              {monthlyPrice
                ? formatMoneyFromFragment(monthlyPrice.totalValue)
                : "—"}
            </Heading>
          </HStack>
          <Text size="xs" className="text-typography-500">
            + {vatLabel} / mes
          </Text>
        </VStack>
      );
    }

    if (isYearly && yearlyPerMonth) {
      return (
        <VStack>
          <HStack space="xs" className="items-baseline">
            <Heading size="2xl" className="tracking-tight text-typography-900">
              {yearlyPerMonth}
            </Heading>
            <Text size="sm" className="text-typography-500">
              / mes
            </Text>
            {savingsRatio ? (
              <Badge action="success" variant="solid" size="sm" rounded="full">
                <BadgeText className="text-2xs">
                  Ahorra {formatPercentage(savingsRatio, 0)}
                </BadgeText>
              </Badge>
            ) : null}
          </HStack>
          <Text size="xs" className="text-typography-500">
            Facturado anualmente
            {monthlyPrice
              ? `, o ${formatMoneyFromFragment(monthlyPrice.totalValue)} mensual`
              : ""}
          </Text>
        </VStack>
      );
    }

    return (
      <VStack>
        <HStack space="xs" className="items-baseline">
          <Heading size="2xl" className="tracking-tight text-typography-900">
            {price ? formatMoneyFromFragment(price.totalValue) : "—"}
          </Heading>
          <Text size="sm" className="text-typography-500">
            / mes
          </Text>
        </HStack>
        <Text size="xs" className="text-typography-400">
          {vatLabel} incluido
        </Text>
      </VStack>
    );
  };

  // Never show a dead CTA: hide the whole card when it needs a sales
  // conversation (Enterprise, or an unpriced plan) but the country has no
  // sales contact link.
  if (needsContact && !salesContactUrl) return null;

  return (
    <Card
      size="md"
      variant="outline"
      className={`relative h-full ${
        recommended ? "border-brand-500 bg-brand-50/40" : "bg-background-0"
      }`}
    >
      {recommended ? (
        <Box className="absolute -top-3 left-5">
          <Badge action="brand" variant="solid" size="sm" rounded="full">
            <BadgeText>Recomendado</BadgeText>
          </Badge>
        </Box>
      ) : null}

      <VStack className="h-full">
        <Heading size="md" className="text-typography-900">
          {plan.name}
        </Heading>
        <Text size="xs" className="mt-1 min-h-[32px] text-typography-500">
          {plan.description}
        </Text>
        {todayAmount ? (
          <Text size="sm" bold className="mt-1 text-brand-600">
            Pagando {todayAmount} hoy
          </Text>
        ) : null}

        <Box className="mt-3">{renderPrice()}</Box>

        <VStack space="xs" className="my-4">
          {(plan.characteristics ?? []).map((feature) => (
            <HStack key={feature} space="sm" className="items-start">
              <Icon as={Check} size="xs" className="mt-1 text-brand-500" />
              <Text size="sm" className="flex-1 text-typography-800">
                {feature}
              </Text>
            </HStack>
          ))}
        </VStack>

        <Box className="mt-auto">
          {needsContact ? (
            <Button
              action="primary"
              variant="outline"
              size="md"
              rounded="lg"
              className="w-full"
              onPress={() => {
                if (salesContactUrl) Linking.openURL(salesContactUrl);
              }}
            >
              <ButtonIcon as={MessageCircle} />
              <ButtonText>Hablemos</ButtonText>
            </Button>
          ) : (
            <Button
              action="brand"
              variant={recommended ? "solid" : "outline"}
              size="md"
              rounded="lg"
              className="w-full"
              isDisabled={!price || loading}
              onPress={() =>
                price &&
                onHire(price.id, plan.name, price.paymentProviderCode ?? null)
              }
            >
              <ButtonText>Contratar {plan.name}</ButtonText>
            </Button>
          )}
        </Box>
      </VStack>
    </Card>
  );
});

PlanTierCard.displayName = "PlanTierCard";

export default PlanTierCard;
